import React, { Component } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import Select from "react-select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import adrActions from "../../_actions/adrActions";
import { contextTypes } from "../../_constants/fileUploadContextTypes";
import AlertModal from "../_common/AlertModal";
import Badge from "../_common/Badge";
import BadgeContainer from "../_common/BadgeContainer";
import { ADR_EDIT } from "../../_constants/actionConstants";

// Server upload failures arrive as a ServiceError-shaped object ({ customMessage, message, ... }),
// not a string, so it can't be interpolated directly into a template literal (renders "[object Object]").
function getUploadErrorMessage(err) {
  if (!err) return "Unknown error.";
  if (typeof err === "string") return err;
  return err.customMessage || err.message || err.title || "Unknown error.";
}

class AdrFileUploadModalV3 extends Component {
  state = {
    files: [], // A "holding area" for files chosen to be uploaded
    formData: [], // Initialized at CDM
    status: {
      deleting: false,
      uploading: false,
      errorOnUpload: null,
    },
    alerts: {
      showAlert_dupFileFound: false,
      showAlert_uploadError: false,
      showAlert_fileSizeError: false,
    },
    skippedFiles: [],
    uploadProgress: { current: 0, total: 0 }
  };

  attachmentTypeOptions = [
    {
      value: 0,
      label: "Photo (General)",
    },
    {
      value: 1,
      label: "Tentative Agreement",
    },
    {
      value: 2,
      label: "Inspection Release Tag",
    },
    {
      value: 3,
      label: "Mini Memo",
    },
    {
      value: 4,
      label: "Other",
    },
  ];

  componentDidMount() {
    const { locationAttachments, commentAttachments } = this.props;

    // At mount, initialize the state based on incoming props
    if (locationAttachments || commentAttachments) this.updateAttachmentList();
  }

  updateAttachmentList = () => {
    const { locationAttachments, commentAttachments } = this.props;
    let formData = [];
    if (locationAttachments) {
      locationAttachments.forEach((la) => {
        let ato = this.attachmentTypeOptions.find((o) => o.value === la.attachmentType);
        if (!ato) ato = this.attachmentTypeOptions[0];
        formData.push({ ...la, selectedAttachmentType: ato });
      });
    } else if (commentAttachments) {
      commentAttachments.forEach((ca) => {
        let ato = this.attachmentTypeOptions.find((o) => o.value === ca.attachmentType);
        if (!ato) ato = this.attachmentTypeOptions[0];
        formData.push({ ...ca, selectedAttachmentType: ato });
      });
    }
    this.setState({ formData });
  };

  handleChange_fileInput = (e) => {
    // Must shallow-copy
    let existFiles = [...this.state.files];
    const newFiles = Array.from(e.target.files);
    const files = existFiles.concat(newFiles);
    this.setState({ files });
  };

  removeFileToUpload = (fileIndex) => {
    // Must shallow-copy
    let files = [...this.state.files];
    files.splice(fileIndex, 1);
    this.setState({ files });
  };

  toggleAlert_fileSizeError = () =>
    this.setState({
      alerts: {
        ...this.state.alerts,
        showAlert_fileSizeError: !this.state.alerts.showAlert_fileSizeError,
      },
    });

  handleAlert_fileSizeError = () => (e) => {
    e.preventDefault();
    this.toggleAlert_fileSizeError();
    this.setState({ skippedFiles: [] }); // Clear skipped files after dismissing
  };

  handleSubmit_uploadFile = async (e) => {
    e.preventDefault();
    const {
      dispatch,
      tabId,
      fileUploadContext,
      locationAttachments: la,
      commentAttachments: ca,
      markAdrDirty,
    } = this.props;
    const { files } = this.state;

    // Screen chosen files for duplicates
    let dupFound = false;
    const fileNames = files.map((f) => f.name);
    if (la) {
      for (let i = 0; i < la.length; i++) {
        if (fileNames.includes(la[i].filePath.replace(/^.*[\\/]/, ""))) {
          dupFound = true;
          break;
        }
      }
    }
    if (ca) {
      for (let i = 0; i < ca.length; i++) {
        if (fileNames.includes(ca[i].filePath.replace(/^.*[\\/]/, ""))) {
          dupFound = true;
          break;
        }
      }
    }

    if (dupFound) {
      this.toggleAlert_dupFileFound();
      return;
    }

    // Initiate upload one file at a time
    this.setState({
      status: { ...this.state.status, uploading: true },
      skippedFiles: [],
      uploadProgress: { current: 0, total: files.length }
    });

    const contextType = this.getContextType();
    const skippedFiles = [];
    let hasSuccessfulUpload = false;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const formData = new FormData();

      // Update progress before processing each file
      this.setState({
        uploadProgress: { current: i + 1, total: files.length }
      });

      formData.append("0", file);
      formData.append("fullEA", fileUploadContext.fullEA);
      formData.append("targetCategory", fileUploadContext.targetCategory);
      formData.append("reportDate", fileUploadContext.reportDate);
      formData.append("userEmployeeNo", fileUploadContext.userEmployeeNo);
      formData.append("subContext", fileUploadContext.subContext);

      try {
        const responseFiles = await dispatch(adrActions.uploadAttachmentV2(formData));

        hasSuccessfulUpload = true;
        markAdrDirty();

        const uploadedFiles = {
          originalFileNames: responseFiles.map((f) => f.originalFilename),
          filePaths: responseFiles.map((f) => f.fileRelPath),
          imageThumbnailFilePaths: responseFiles.map((f) => f.imageThumbnailFileRelPath),
          thumbnailBase64s: responseFiles.map((f) => f.thumbnailBase64),
          createdOn: responseFiles[0]?.createdOn,
          descriptions: responseFiles.map((f) => buildExifDescription(f)),
        };

        if (contextType === contextTypes.LOCWP) {
          const params = {
            tabId,
            wpTabId: fileUploadContext.wpTabId,
            clientId: fileUploadContext.locWpClientId,
            uploadedFiles,
          };
          dispatch({
            type: ADR_EDIT.LOCATION_WORKPERFORMED.ATTACHMENTS.ADD,
            ...params,
          });
          dispatch(
            adrActions.addTempFilePaths(tabId, [
              ...uploadedFiles.filePaths,
              ...uploadedFiles.imageThumbnailFilePaths,
            ])
          );
        } else if (contextType === contextTypes.GC) {
          const params = {
            tabId,
            clientId: fileUploadContext.gcClientId,
            uploadedFiles,
          };
          dispatch({ type: ADR_EDIT.GENERAL_COMMENTS.ATTACHMENTS.ADD, ...params });
        }

        this.updateAttachmentList();

      } catch (err) {
        // Check if error is due to file size limit and skips it
        const isFileSizeError =
          err?.type?.includes("BadHttpRequestException") ||
          err?.message?.toLowerCase().includes("request body too large") ||
          err?.message?.toLowerCase().includes("max request body size");
        if (isFileSizeError) {
          skippedFiles.push(file.name);
          continue;
        } else {
          // Reset state when encoutering error
          this.setState({
            status: { ...this.state.status, uploading: false, errorOnUpload: err },
            skippedFiles,
            uploadProgress: { current: 0, total: 0 }
          });
          this.toggleAlert_uploadError();
          return;
        }
      }
    }

    // Reset state after all files uploaded
    this.setState({
      status: { ...this.state.status, uploading: false },
      files: [],
      skippedFiles,
      uploadProgress: { current: 0, total: 0 }
    });

    // Alert for any files over 30mb
    if (skippedFiles.length > 0) {
      this.toggleAlert_fileSizeError();
    }

    // Execute custom callback if provided and at least one upload succeeded
    if (hasSuccessfulUpload &&
      fileUploadContext.customCallback &&
      typeof fileUploadContext.customCallback === "function") {
      fileUploadContext.customCallback();
    }
  };

  handleChange = (fileRowIndex) => (e) => {
    const formData = this.state.formData.map((fd) => ({ ...fd }));
    if (formData[fileRowIndex]) {
      formData[fileRowIndex][e.target.name] = e.target.value;
      this.setState({ formData });
    }
  };

  handleChange_select =
    (fileRowIndex, fieldName, fieldOptionName) => (selectedOption) => {
      const { dispatch, tabId, fileUploadContext } = this.props;

      const formData = this.state.formData.map((fd) => ({ ...fd }));
      if (formData[fileRowIndex]) {
        formData[fileRowIndex][fieldOptionName] = selectedOption;
        formData[fileRowIndex][fieldName] = selectedOption.value;
      }

      const contextType = this.getContextType();
      if (contextType === contextTypes.LOCWP) {
        dispatch({
          type: ADR_EDIT.LOCATION_WORKPERFORMED.ATTACHMENTS.UPDATE,
          tabId,
          wpTabId: fileUploadContext.wpTabId,
          clientId: fileUploadContext.locWpClientId,
          locWpaIndex: fileRowIndex,
          fields: { [fieldName]: selectedOption.value },
        });
      } else if (contextType === contextTypes.GC) {
        // dispatch gca update
        dispatch({
          type: ADR_EDIT.GENERAL_COMMENTS.ATTACHMENTS.UPDATE,
          tabId,
          clientId: fileUploadContext.gcClientId,
          gcaIndex: fileRowIndex,
          fields: { [fieldName]: selectedOption.value },
        });
      }
      this.setState({ formData });
    };

  handleBlur = (fileRowIndex) => (e) => {
    const { dispatch, tabId, fileUploadContext } = this.props;
    const contextType = this.getContextType();
    if (contextType === contextTypes.LOCWP) {
      const params = {
        tabId,
        wpTabId: fileUploadContext.wpTabId,
        clientId: fileUploadContext.locWpClientId,
        locWpaIndex: fileRowIndex,
        fields: { [e.target.name]: e.target.value },
      };
      dispatch({
        type: ADR_EDIT.LOCATION_WORKPERFORMED.ATTACHMENTS.UPDATE,
        ...params,
      });
    } else if (contextType === contextTypes.GC) {
      // dispatch gca update
      dispatch({
        type: ADR_EDIT.GENERAL_COMMENTS.ATTACHMENTS.UPDATE,
        tabId,
        clientId: fileUploadContext.gcClientId,
        gcaIndex: fileRowIndex,
        fields: { [e.target.name]: e.target.value },
      });
    }
  };

  handleClick_delete = (fileRowIndex) => (e) => {
    const { dispatch, tabId, fileUploadContext } = this.props;
    e.preventDefault();
    const contextType = this.getContextType();
    if (contextType === contextTypes.LOCWP) {
      const params = {
        tabId,
        wpTabId: fileUploadContext.wpTabId,
        clientId: fileUploadContext.locWpClientId,
        locWpaIndex: fileRowIndex,
      };
      dispatch(adrActions.toggleDeleteAttachment(params, contextTypes.LOCWP)).then(
        this.updateAttachmentList
      );
    } else if (contextType === contextTypes.GC) {
      const params = {
        tabId,
        clientId: fileUploadContext.gcClientId,
        gcaIndex: fileRowIndex,
      };
      dispatch(adrActions.toggleDeleteAttachment(params, contextTypes.GC)).then(
        this.updateAttachmentList
      );
    }
    this.setState({ deleting: !this.state.status.deleting });
  };

  handleClick_close = (e) => {
    e.preventDefault();
    this.props.fileUploadContext.toggleModal_fileUpload();
  };

  getAttachment = (fileRowIndex) => {
    const { locationAttachments: la, commentAttachments: ca } = this.props;
    return la ? la[fileRowIndex] : ca ? ca[fileRowIndex] : null;
  };

  getContextType = () => {
    const { locationAttachments: la, commentAttachments: ca } = this.props;
    return la ? contextTypes.LOCWP : ca ? contextTypes.GC : contextTypes.UNKNOWN;
  };

  handleUnknownContextError = () =>
    alert(
      "Error: unknown file upload context encountered. Please contact the system administrator."
    );

  toggleAlert_dupFileFound = () =>
    this.setState({
      alerts: {
        ...this.state.alerts,
        showAlert_dupFileFound: !this.state.alerts.showAlert_dupFileFound,
      },
    });

  toggleAlert_uploadError = () =>
    this.setState({
      alerts: {
        ...this.state.alerts,
        showAlert_uploadError: !this.state.alerts.showAlert_uploadError,
      },
    });

  handleAlert_dupFileFound = () => (e) => {
    e.preventDefault();
    this.toggleAlert_dupFileFound();
  };

  handleAlert_uploadError = () => (e) => {
    e.preventDefault();
    this.toggleAlert_uploadError();
  };

  handleClick_openFile = (filePath) => (e) => {
    e.preventDefault();
    this.props.dispatch(adrActions.openFile(filePath));
  };

  getThumbnail = (fileRowIndex) => {
    const attachment = this.getAttachment(fileRowIndex);
    const fileExt = attachment.filePath.split(".").pop();
    let content;
    if (attachment.imageThumbnailFilePath)
      content = (
        <img
          src={attachment.thumbnailBase64}
          alt="(Cannot display thumbnail)"
          height="90"
          width="auto"
        />
      );
    else if (fileExt === "pdf")
      content = <FontAwesomeIcon icon={["far", "file-pdf"]} className="fa-fw fa-4x" />;
    else if (fileExt === "xlsx")
      content = <FontAwesomeIcon icon={["far", "file-excel"]} className="fa-fw fa-4x" />;
    else if (fileExt === "docx")
      content = <FontAwesomeIcon icon={["far", "file-word"]} className="fa-fw fa-4x" />;
    else content = <FontAwesomeIcon icon={["far", "file"]} className="fa-fw fa-4x" />;

    return (
      <div onClick={this.handleClick_openFile(attachment.filePath)} className="pointer-cursor">
        {content}
      </div>
    );
  };

  getFileName = (fileRowIndex) => this.getAttachment(fileRowIndex).originalFileName; //filePath.replace(/^.*[\\/]/, "");

  renderDeleteButton = (attachment, fileRowIndex) => {
    const { status: adrStatus } = this.props;
    if (!attachment.isDeleted)
      return (
        <div className="row">
          <button
            type="button"
            name="delAttach"
            id={`delAttach_${fileRowIndex}`}
            className="btn btn-sm btn-danger"
            onClick={this.handleClick_delete(fileRowIndex)}
            disabled={adrStatus.isReadOnly}
          >
            Remove
          </button>
        </div>
      );
    else if (attachment.isDeleted)
      return (
        <React.Fragment>
          <div className="row">
            <button
              type="button"
              name="cancelDelAttach"
              id={`cancelDelAttach_${fileRowIndex}`}
              className="btn btn-sm btn-success"
              onClick={this.handleClick_delete(fileRowIndex)}
              disabled={adrStatus.isReadOnly}
            >
              Undo Remove
            </button>
          </div>
          <div className="row">
            <span>
              <small>This attachment will be deleted after you save this DR.</small>
            </span>
          </div>
        </React.Fragment>
      );
  };

  render() {
    const { fileUploadContext, className, status: adrStatus } = this.props;
    const { files, status, alerts, formData, uploadProgress } = this.state;
    const closeBtn = (
      <button className="close" onClick={fileUploadContext.toggleModal_fileUpload}>
        <FontAwesomeIcon icon="times" className="fa-fw text-white" />
      </button>
    );
    return (
      <React.Fragment>
        <Modal
          isOpen={fileUploadContext.showModal_fileUpload}
          toggle={fileUploadContext.toggleModal_fileUpload}
          className={className}
          fade={false}
          size="lg"
        >
          <ModalHeader
            toggle={fileUploadContext.toggleModal_fileUpload}
            className="bg-info text-white"
            close={closeBtn}
          >
            <FontAwesomeIcon icon="paperclip" className="fa-fw mr-2" />
            Attachments
            {status.uploading && (
              <span>
                <FontAwesomeIcon icon="spinner" className="loading-spinner mr-2" />
                <small>Uploading {uploadProgress.current} of {uploadProgress.total}...</small>
              </span>
            )}
          </ModalHeader>
          <ModalBody>
            <div className="card mb-2">
              <div className="card-body">
                <h5>
                  <FontAwesomeIcon icon="upload" className="fa-fw mr-2" />
                  Upload
                </h5>
                <form
                  id="fileUploadForm"
                  onSubmit={this.handleSubmit_uploadFile}
                  autoComplete={"off"}
                  encType="multipart/form-data"
                >
                  <div className="form-row form-group mb-2">
                    <div className="col-sm-8">
                      <div className="custom-file">
                        <input
                          type="file"
                          id="file"
                          name="file"
                          className="custom-file-input"
                          onChange={this.handleChange_fileInput}
                          multiple
                          disabled={adrStatus.isReadOnly}
                        />
                        <label htmlFor="file" className="custom-file-label">
                          Choose files...
                        </label>
                      </div>
                    </div>
                    <div className="col-sm-4">
                      <input
                        className="btn btn-primary mr-2"
                        name="submit_uploadFile"
                        type="submit"
                        form="fileUploadForm"
                        onClick={this.handleSubmit_uploadFile}
                        value="Upload"
                        disabled={
                          files.length === 0 || status.uploading || adrStatus.isReadOnly
                        }
                      />
                      {status.uploading && (
                        <span>
                          <FontAwesomeIcon
                            icon="spinner"
                            className="loading-spinner mr-2"
                          />
                          <small>Uploading...</small>
                        </span>
                      )}
                    </div>
                  </div>
                  {files.length > 0 && (
                    <BadgeContainer>
                      {files.map((f, index) => (
                        <Badge
                          className="mr-1"
                          key={`fileLabelIndex_${index}`}
                          itemId={index}
                          clickItem={() => {}}
                          deleteItem={this.removeFileToUpload}
                          label={f.name}
                          showDelete={true}
                        />
                      ))}
                    </BadgeContainer>
                  )}
                </form>
              </div>
            </div>
            <div className="card">
              <div className="card-body">
                <h5>
                  <FontAwesomeIcon icon="folder-open" className="fa-fw mr-2" />
                  Uploaded Files
                </h5>
                <ul className="list-group list-group-flush">
                  <li className="list-group-item py-1 bg-lightgray">
                    <div className="row">
                      <div className="col-sm-4 pl-1">
                        <b>Thumbnail</b>
                      </div>
                      <div className="col-sm-6 pl-1">
                        <b>Attachment Info</b>
                      </div>
                    </div>
                  </li>
                  {formData &&
                    formData.map((attachment, fileRowIndex) => (
                      <li
                        key={`attachRowKey_${fileRowIndex}`}
                        className="list-group-item"
                      >
                        <div className="row">
                          <div className="col-sm-4">
                            <div className="row">{this.getThumbnail(fileRowIndex)}</div>
                            <div className="row">
                              <small>{this.getFileName(fileRowIndex)}</small>
                            </div>
                          </div>
                          <div className="col-sm-6">
                            <div className="form-row mb-1">
                              <label
                                htmlFor={`attachType_${fileRowIndex}`}
                                className="col-sm-3 my-auto pl-0"
                              >
                                <b>Type:</b>
                              </label>
                              <div className="col-sm-9 ml-0 px-0">
                                <Select
                                  name="attachmentType"
                                  id={`attachmentType_${fileRowIndex}`}
                                  value={attachment.selectedAttachmentType}
                                  options={this.attachmentTypeOptions}
                                  onChange={this.handleChange_select(
                                    fileRowIndex,
                                    "attachmentType",
                                    "selectedAttachmentType"
                                  )}
                                  isDisabled={adrStatus.isReadOnly}
                                />
                              </div>
                            </div>
                            {attachment.attachmentType === 2 && (
                              <div className="form-row">
                                <label
                                  htmlFor={`attachMatlLotNo_${fileRowIndex}`}
                                  className="col-sm-3 my-auto pl-0"
                                >
                                  <b>Lot No:</b>
                                </label>
                                <input
                                  type="text"
                                  name="materialLotNumber"
                                  id={`attachmentMaterialLotNo_${fileRowIndex}`}
                                  className="form-control col-sm-9 mb-1"
                                  value={attachment.materialLotNumber}
                                  onChange={this.handleChange(fileRowIndex)}
                                  onBlur={this.handleBlur(fileRowIndex)}
                                  disabled={adrStatus.isReadOnly}
                                />
                              </div>
                            )}
                            <div className="form-row">
                              <label
                                htmlFor={`attachDescription_${fileRowIndex}`}
                                className="mb-0"
                              >
                                <b>Description:</b>
                              </label>
                              <textarea
                                name="description"
                                id={`attachmentDescription_${fileRowIndex}`}
                                className="form-control w-100"
                                rows="3"
                                value={attachment.description}
                                onChange={this.handleChange(fileRowIndex)}
                                onBlur={this.handleBlur(fileRowIndex)}
                                disabled={adrStatus.isReadOnly}
                              />
                            </div>
                          </div>
                          <div className="col-sm-2">
                            {this.renderDeleteButton(attachment, fileRowIndex)}
                          </div>
                        </div>
                      </li>
                    ))}
                </ul>
              </div>
            </div>
          </ModalBody>
          <ModalFooter className="bg-lightgray">
            <div className="form-row form-group mb-0">
              <button
                className="btn btn-secondary mr-2"
                color="secondary"
                onClick={this.handleClick_close}
              >
                Close
              </button>
            </div>
          </ModalFooter>
        </Modal>
        {alerts.showAlert_dupFileFound && (
          <AlertModal
            alertTitle="File(s) Already Exist"
            alertTitleClass="bg-warning"
            alertTitleIcon="exclamation-triangle"
            alertText={`One of the files you're trying to upload already exists.
              \nYour request could not be completed.`}
            acceptCaption={"OK"}
            acceptClass={"danger"}
            provideReject={false}
            handleAlertAction={this.handleAlert_dupFileFound}
            toggleAlertModal={this.toggleAlert_dupFileFound}
            showAlertModal={alerts.showAlert_dupFileFound}
          />
        )}
        {alerts.showAlert_uploadError && (
          <AlertModal
            alertTitle="Error"
            alertTitleClass="bg-danger text-white"
            alertTitleIcon="exclamation-triangle"
            alertText={`An error occurred while attempting to upload file(s).
              \n${getUploadErrorMessage(status.errorOnUpload)}\nPlease contact the system administrator.`}
            acceptCaption={"OK"}
            acceptClass={"danger"}
            provideReject={false}
            handleAlertAction={this.handleAlert_uploadError}
            toggleAlertModal={this.toggleAlert_uploadError}
            showAlertModal={alerts.showAlert_uploadError}
          />
        )}
        {alerts.showAlert_fileSizeError && (
          <AlertModal
            alertTitle="File Size Limit Exceeded"
            alertTitleClass="bg-warning"
            alertTitleIcon="exclamation-triangle"
            alertText={`The following file(s) exceeded the server size limit of 30MB and were not uploaded:\n\n${this.state.skippedFiles.join("\n")}\n\nPlease reduce the file size and try again.`}
            acceptCaption={"OK"}
            acceptClass={"warning"}
            provideReject={false}
            handleAlertAction={this.handleAlert_fileSizeError}
            toggleAlertModal={this.toggleAlert_fileSizeError}
            showAlertModal={alerts.showAlert_fileSizeError}
          />
        )}
      </React.Fragment>
    );
  }
}

const getLocationAttachments = (adr, wpTabId, locWpClientId) => {
  if (!adr || wpTabId == null || locWpClientId == null) return null;
  const wpc = adr.workPerformedClient[wpTabId];
  if (!wpc) return null;
  const locWp = wpc.locationWorkPerformed.find(
    (locWp) => locWp.clientId === locWpClientId
  );
  if (!locWp) return null;
  return locWp.attachments || [];
};

const getCommentAttachments = (adr, gcClientId) => {
  if (!adr || gcClientId == null) return null;
  const gc = adr.generalComments.find((gc) => gc.clientId === gcClientId);
  if (!gc) return null;
  return gc.commentAttachments || [];
};

const mapStateToProps = (state, ownProps) => ({
  appSettings: state.appSettings,
  locationAttachments: getLocationAttachments(
    state.adr.openAdrs[ownProps.tabId],
    ownProps.fileUploadContext.wpTabId,
    ownProps.fileUploadContext.locWpClientId
  ),
  commentAttachments: getCommentAttachments(
    state.adr.openAdrs[ownProps.tabId],
    ownProps.fileUploadContext.gcClientId
  ),
});

AdrFileUploadModalV3.propTypes = {
  tabId: PropTypes.number.isRequired,
  locationAttachments: PropTypes.array,
  commentAttachments: PropTypes.array,
};

export default connect(mapStateToProps)(AdrFileUploadModalV3);

function buildExifDescription(file) {
  const parts = [];
  if (file.exifDateTaken) {
    const d = new Date(file.exifDateTaken);
    parts.push(`Taken: ${d.toLocaleDateString()} ${d.toLocaleTimeString()}`);
  }
  const lat = Number(file.exifLatitude);
  const lng = Number(file.exifLongitude);
  if (file.exifLatitude != null && file.exifLongitude != null && Number.isFinite(lat) && Number.isFinite(lng)) {
    parts.push(`GPS: ${lat.toFixed(6)}, ${lng.toFixed(6)}`);
  }
  const alt = Number(file.exifAltitude);
  if (file.exifAltitude != null && Number.isFinite(alt)) {
    parts.push(`Altitude: ${alt.toFixed(1)} m`);
  }
  return parts.join("\n");
}
